import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  apiError,
  changeMenuActived,
  isTransactionNotFound,
  isTransactionWithError,
  loginSuccess,
  loginUserByToken,
  logoutUser,
  showTopbarInsideAction,
} from "../store/actions";
import BizinBackend from "../helpers/bizin_helper";
import { LOGIN } from "../helpers/routes_helper";
import { authProtectedRoutes } from "./routes";
import { AuthProtectedContext } from "./AuthProtectedContext";
import useGetCsrfToken from "../Hooks/useGetCsrfToken";
import { decode } from "js-base64";

const regexTransation = /^[A-Z][A-Z0-9_]*[A-Z0-9]$/;

const AuthProtected = (props) => {
  const dispatch = useDispatch();
  const csrfToken = useGetCsrfToken();
  const { user, menusSidebar, isModalPage, isIframePage } = useSelector(
    (state) => ({
      user: state.login.profile,
      menusSidebar: state.login.user,
      isModalPage: state.Layout.isModalPage,
      isIframePage: state.Layout.isIframePage,
    }),
  );

  const { pathname, search } = useLocation();

  const [isLoading, setIsLoading] = useState(false);

  const getUserByToken = async (token) => {
    const data = await BizinBackend.executeProcedure(
      { 1: decode(token), 2: "DEFENDER" },
      "FRONT_Token_Acesso_Verificar",
      csrfToken,
    );

    if (data[0] && data[0]?.statusCode == 200) {
      const { Email } = data[0];
      dispatch(
        loginUserByToken({
          email: Email,
          token: decode(token),
          _csrf: csrfToken,
        }),
      );
    } else {
      dispatch(
        isTransactionWithError({
          payload: { status: 401, statusText: "Unauthorized." },
        }),
      );
    }
  };

  const checkUser = async () => {
    const params = new URLSearchParams(search);
    if (user && user?.ID_Usuario && menusSidebar?.access_token) {
      if (
        params.get("token") &&
        menusSidebar?.resultset_1[0].Token_Acesso !==
          decode(params.get("token"))
      ) {
        dispatch(logoutUser(null));
        getUserByToken(params.get("token"));
      } else {
        try {
          await BizinBackend.checkAccessToken(menusSidebar.access_token);
        } catch (error) {
          dispatch(
            apiError(
              `Sessão expirada. ${isModalPage || isIframePage ? "Por favor recarrege a pagina" : ""}`,
            ),
          );
          dispatch(logoutUser(null));
        }
      }
    }
  };

  const findMenuInTree = (menus, targetCodigo) => {
    for (const menu of menus || []) {
      if (
        menu?.Codigo_Transacao?.toUpperCase() === targetCodigo.toUpperCase() &&
        menu?.Disponivel === "S"
      ) {
        return menu;
      }
      if (menu.childrens && menu.childrens.length > 0) {
        const foundInChildren = findMenuInTree(menu.childrens, targetCodigo);
        if (foundInChildren) {
          return foundInChildren;
        }
      }
    }
    return null;
  };

  const checkPathname = async () => {
    try {
      let id_menu = null;

      if (menusSidebar && menusSidebar?.resultset_4) {
        const pathnameUpper = pathname.replace("/", "").toUpperCase();
        const menuAtivo = findMenuInTree(
          menusSidebar.resultset_4,
          pathnameUpper,
        );

        if (menuAtivo && menuAtivo.ID_Menu) {
          id_menu = menuAtivo.ID_Menu;
        }
      }

      if (id_menu === null) {
        const savedMenu = localStorage.getItem("lastClickedTopInsiderMenu");
        if (savedMenu) {
          try {
            const parsedMenu = JSON.parse(savedMenu);
            if (parsedMenu && parsedMenu.id) {
              id_menu = parsedMenu.id;
            }
          } catch (e) {}
        }
      }
    } catch (error) {
      if ([401, 403].includes(error.status)) dispatch(logoutUser());
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(
    useCallback(() => {
      checkUser();
    }, [user]),
    [],
  );

  useEffect(() => {
    if (
      user &&
      new RegExp(regexTransation).test(pathname.toUpperCase().replace("/", ""))
    ) {
      setIsLoading(true);
      checkPathname();
    } else {
      dispatch(isTransactionNotFound(true));
    }

    if (menusSidebar && menusSidebar?.resultset_4) {
      const pathnameUpper = pathname.replace("/", "").toUpperCase();
      const menuAtivo = findMenuInTree(menusSidebar.resultset_4, pathnameUpper);

      if (menuAtivo) {
        dispatch(changeMenuActived(menuAtivo));
      }
    }
  }, [pathname]);

  if (!user && isIframePage && window) {
    window.location.href = `${window.location.origin}${window.location.pathname + window.location.search}`;
  }

  if (!user && window) {
    localStorage.setItem(
      "UrlOriginNoAuth",
      window.location.pathname + window.location.search,
    );
    return (
      <Navigate to={{ pathname: LOGIN, state: { from: props.location } }} />
    );
  }

  if (!user) {
    localStorage.setItem(
      "UrlOriginNoAuth",
      window.location.pathname + window.location.search,
    );
    return (
      <Navigate to={{ pathname: LOGIN, state: { from: props.location } }} />
    );
  }

  if (user.Alterar_Senha === "S") {
    return (
      <Navigate to={{ pathname: "/new-pw", state: { from: props.location } }} />
    );
  }

  return (
    <AuthProtectedContext.Provider value={{ isLoading }}>
      {props.children}
    </AuthProtectedContext.Provider>
  );
};

export { AuthProtected };
